/**
 * @ngdoc controller
 * @memberOf app.login
 * @name LoginController
 * @description Controller used for handling the login of the user
 */
export default class LoginController {
  constructor(LoginService, $state, Flash, AuthService) {

    /**
     * @memberOf LoginController
     * @type {service}
     * @description Instance of LoginService
     */
    this.loginService = LoginService;

    /**
     * @memberOf LoginController
     * @type {service}
     * @description ui-router's state service for interacting with states of application
     */
    this.$state = $state;

    /**
     * @memberOf LoginController
     * @type {service}
     * @description ngFlash flash service for flashing messages on screen
     */
    this.flash = Flash;

    /**
     * @memberOf LoginController
     * @type {service}
     * @description Instance of AuthService
     */
    this.AuthService = AuthService;

    /**
     * @memberOf LoginController
     * @type {string}
     * @description Holds the current template for the login view, default is form.html
     */
    this.template = '/partials/form.html';

    /**
     * @memberOf LoginController
     * @type {boolean}
     * @description Holds whether or not the user has clicked the submit button. Used in changing the submit button to
     * disabled and text.
     */
    this.submitting = false;
  }

  /**
   * @memberOf LoginController
   * @param {string} error Error string to insert into template literal
   * @description Creates login error string as a template literal using error param
   * @returns {string} Final string with error inserted
   */
  errorMessage(error) {
    return(`
    <strong>Uh oh!  We encountered a problem.</strong>
    
    <br/>${error} If you no longer have access to that email account or if you have questions, please contact the NIOSH Health Hazard Evaluation Program:

    <br /><br/>NIOSH Health Hazard Evaluation Program 
    <br/>HHERequestHelp@cdc.gov
    <br/>Phone number: 513-841-4382
    `);
  }

  /**
   * @memberOf LoginController
   * @description Changes the template for the login screen.
   */
  changeTemplate() {
    this.template = '/partials/form.html';
  }

  /**
   * @memberOf LoginController
   * @description User has entered email address in form. Calls LoginService's requestToken method to check if the user
   * is a valid user. If not, flash errors on the screen. Otherwise remove user accepted disclaimer with AuthService and
   * change the template to the instructions.
   */
  requestToken() {
    let error = '';

    this.submitting = true;
    this.loginService.requestToken(this.email.toLowerCase().trim()).then((data) => {
      this.flash.clear();
      switch(data.data) {
        case 'success':
          this.template = '/partials/instructions.html';
          this.AuthService.removeAccepted();
          this.submitting = false;
          this.email = '';
          break;
        case 'locked':
          error = 'Due to too many failed login attempts, your account has been locked.'
          this.flash.create('danger', this.errorMessage(error), 0, { container: 'invalid' });
          this.submitting = false;
          break;
        case 'Unauthorized':
        default:
          error = 'The email address you entered was not found in our system.  Please enter the email address you provided to NIOSH staff when we were in your workplace.'
          this.flash.create('danger', this.errorMessage(error), 0, { container: 'invalid' });
          this.submitting = false;
          break;
      }
    });
  }

  /**
   * @memberOf LoginController
   * @description Checks if the user login failed.
   * @returns {Boolean} True if login failed, false otherwise
   */
  isFailedLogin() {
    return this.$state.params.failedLogin;
  }

  /**
   * @memberOf LoginController
   * @description Creates a flash on the view about login link expiring.
   */
  flashLoginError() {
    let message = 'The  login link that you used has expired.  Please enter a your valid email below to request a new link.';
    this.flash.clear();
    this.flash.create('danger', message, 0, { container: 'invalid' });
  }
}

LoginController.$inject = ['LoginService', '$state', 'Flash', 'AuthService'];



// WEBPACK FOOTER //
// ./src/client/app/login/login.controller.js